// petconnect-v2/frontend/src/components/features/Search/PublicSearchPage.js
// MERGED:
//   - Result cards are clickable → navigate to /post/:id
//   - owner_message field displayed
//   - Image container uses proper aspect-ratio (object-contain)

import React, { useState } from 'react';
import { Search, MapPin, LogIn, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SearchFormComponent from './SearchFormComponent';
import Pagination from '../../common/Pagination';
import NoResultsModal from '../../common/NoResultsModal';
import { API_URL } from '../../../utils/constants';

// ── Safe image with fallback ──────────────────────────────────────────────────
const SafeImage = ({ post, className = '' }) => {
  const [errored, setErrored] = useState(false);
  const src =
    Array.isArray(post.media_urls) && post.media_urls.length > 0
      ? post.media_urls[0]
      : typeof post.media_url === 'string' && post.media_url
      ? post.media_url
      : null;

  if (!src || errored) {
    return <div className={`flex items-center justify-center bg-gray-100 text-4xl ${className}`}>🐾</div>;
  }
  return (
    <img
      src={src}
      alt={post.pet_name || 'Pet photo'}
      className={className}
      onError={() => setErrored(true)}
    />
  );
};

// Normalise so media_urls is always an array
const normalisePost = (post) => {
  if (Array.isArray(post.media_urls)) return post;
  if (post.media_url) return { ...post, media_urls: [post.media_url] };
  return { ...post, media_urls: [] };
};

const publicSearch = async (searchData) => {
  const response = await fetch(`${API_URL}/search/public`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(searchData),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Search failed' }));
    throw new Error(error.detail || error.error || 'Search failed');
  }
  return response.json();
};

const PublicSearchPage = ({ onLoginClick }) => {
  const navigate = useNavigate();
  const [searchResults, setSearchResults]       = useState([]);
  const [searching, setSearching]               = useState(false);
  const [currentPage, setCurrentPage]           = useState(1);
  const [hasSearched, setHasSearched]           = useState(false);
  const [showNoResultsModal, setShowNoResultsModal] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const resultsPerPage = 25;

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const handleSearch = async (searchParams) => {
    setSearching(true);
    setCurrentPage(1);
    setHasSearched(true);
    setLastSearchParams(searchParams);

    try {
      const searchData = {
        pet_name:    searchParams.pet_name || '',
        animal_type: searchParams.animal_type,
        breed:       searchParams.animal_type === 'dog' ? searchParams.breed : null,
        sex:         searchParams.sex !== 'all' ? searchParams.sex : null,
        description: searchParams.description || '',
        location:    searchParams.location,
        latitude:    searchParams.latitude,
        longitude:   searchParams.longitude,
        radius_km:   searchParams.radius_km,
        category:    searchParams.category !== 'all' ? searchParams.category : null,
      };

      const data = await publicSearch(searchData);
      const results = (data.posts || []).map(normalisePost);
      setSearchResults(results);
      if (results.length === 0) setShowNoResultsModal(true);
    } catch (err) {
      console.error('Search error:', err);
      alert(`Search failed: ${err.message}`);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages     = Math.ceil(searchResults.length / resultsPerPage);
  const startIndex     = (currentPage - 1) * resultsPerPage;
  const endIndex       = startIndex + resultsPerPage;
  const currentResults = searchResults.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-8">

      {/* Header */}
      <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-3xl">🐾</span>
            <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              PetConnectAI Search
            </h1>
          </div>
          {onLoginClick && (
            <button
              onClick={onLoginClick}
              className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-semibold text-sm md:text-base"
            >
              <LogIn size={18} />
              Login / Sign Up
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Info Banner */}
        <div className="bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-blue-300 rounded-xl p-6 md:p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-3">🔍 Search for Lost & Found Pets</h2>
          <p className="text-gray-700 mb-4">Use our advanced search to find pets by location, type, breed, and more. No login required!</p>
          <p className="text-sm text-gray-600">
            💡 <strong>Want to create posts or message users?</strong> Sign up for a free account to unlock all features.
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Search size={28} className="text-blue-500" />
            Search for Pets
          </h3>
          <SearchFormComponent onSearch={handleSearch} isLoading={searching} showCategoryFilter={true} />
        </div>

        {/* Results Grid */}
        {searchResults.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Search Results</h3>
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1}–{Math.min(endIndex, searchResults.length)} of {searchResults.length}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
              {currentResults.map((post) => {
                const mediaUrls = post.media_urls;
                return (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/post/${post.id}`)}
                    className="bg-gray-50 rounded-xl border border-gray-200 hover:shadow-lg hover:border-purple-300 transition overflow-hidden flex flex-col cursor-pointer group"
                  >
                    {/* Image */}
                    <div className="relative w-full" style={{ paddingTop: '66.67%' }}>
                      {mediaUrls.length > 0 ? (
                        <>
                          <SafeImage
                            post={post}
                            className="absolute inset-0 w-full h-full object-contain bg-gray-50 group-hover:opacity-95 transition"
                          />
                          {mediaUrls.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs font-bold px-2 py-1 rounded-full">
                              +{mediaUrls.length - 1} more
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                          <span className="text-5xl">🐾</span>
                        </div>
                      )}
                      {/* Category badge */}
                      <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold text-white ${
                        post.category === 'found'    ? 'bg-green-500' :
                        post.category === 'missing'  ? 'bg-red-500'   :
                        post.category === 'adoption' ? 'bg-pink-500'  : 'bg-blue-500'
                      }`}>
                        {post.category === 'found'    ? '🟢 FOUND'    :
                         post.category === 'missing'  ? '🔴 MISSING'  :
                         post.category === 'adoption' ? '❤️ ADOPTION' : post.category.toUpperCase()}
                      </div>
                      {/* View icon */}
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition">
                        <ExternalLink size={12} />
                      </div>
                    </div>

                    <div className="px-4 py-3 flex flex-col flex-1">
                      {/* Author */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-purple-400 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {post.author?.username?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-800 truncate">{post.author?.username || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">📅 {formatDateTime(post.created_at)}</p>
                        </div>
                        {post.status === 'resolved' && (
                          <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded flex-shrink-0">
                            ✓ Reunited
                          </span>
                        )}
                      </div>

                      {/* Pet name */}
                      {post.pet_name && (
                        <h4 className="text-base font-bold text-gray-800 mb-1">
                          🐾 {post.pet_name}
                          {post.pet_type && <span className="text-sm font-normal text-gray-600"> ({post.pet_type})</span>}
                        </h4>
                      )}

                      <p className="text-sm text-gray-700 mb-2 line-clamp-3 flex-1">{post.content}</p>

                      {/* Owner message */}
                      {post.owner_message && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                          <p className="text-xs font-semibold text-amber-800 mb-0.5">💬 Message from Owner:</p>
                          <p className="text-sm text-amber-900 line-clamp-2">{post.owner_message}</p>
                        </div>
                      )}

                      {post.location_name && (
                        <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                          <MapPin size={12} />
                          <span className="truncate">{post.location_name}</span>
                        </div>
                      )}

                      {post.contact_info && (
                        <div className="text-sm text-blue-600 font-semibold mb-2">📞 {post.contact_info}</div>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t mt-auto">
                        <div className="flex gap-3">
                          <span>❤️ {post.likes_count}</span>
                          <span>💬 {post.comments_count}</span>
                        </div>
                        <span className="text-purple-500 font-semibold opacity-0 group-hover:opacity-100 transition">
                          View post →
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            )}
          </div>
        )}

        {/* Pre-search empty state */}
        {!hasSearched && !searching && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Search size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 text-lg">Fill in search criteria and click "Search Lost & Found Pets"</p>
          </div>
        )}
      </main>

      <NoResultsModal
        isOpen={showNoResultsModal}
        onClose={() => setShowNoResultsModal(false)}
        searchParams={lastSearchParams}
      />
    </div>
  );
};

export default PublicSearchPage;